import jsPDF from "jspdf";
import { VitalityReportData, AXE1_CHECKBOXES, AXE2_CHECKBOXES, AXE3_CHECKBOXES } from "@/components/session-report/VitalityReportForm";
import { BRAND } from "@/config/brand";
import logoImage from "@/assets/regard-de-nature-logo.png";

const colors = {
  primary: BRAND.colors.rgb.forestGreen,
  secondary: BRAND.colors.rgb.softAccent,
  accent: BRAND.colors.rgb.gold,
  background: BRAND.colors.rgb.lightBackground,
  text: BRAND.colors.rgb.text,
  textLight: BRAND.colors.rgb.textLight,
  white: BRAND.colors.rgb.white,
};

interface VitalityReportPdfOptions {
  data: VitalityReportData;
  patientName: string;
  patientDateOfBirth?: string | null;
  patientGender?: string | null;
  accompanimentTitle: string;
  reportDate: string;
  signatureDataUrl?: string;
  notes?: string;
  childInfo?: { name: string; age?: string; gender?: string | null };
}

/** Build the jsPDF doc and return it */
const buildVitalityPdf = (options: VitalityReportPdfOptions): jsPDF => {
  const { data, patientName, patientDateOfBirth, patientGender, accompanimentTitle, reportDate, signatureDataUrl, notes, childInfo } = options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const lineHeight = 6;
  let yPos = margin;

  const checkPageBreak = (extraSpace: number = 15) => {
    if (yPos + extraSpace > pageHeight - margin - 15) {
      doc.addPage();
      yPos = margin;
      doc.setFontSize(8);
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      doc.setFont("helvetica", "italic");
      doc.text("Programme d'hygiene vitale personnalise", pageWidth / 2, margin - 5, { align: "center" });
    }
  };

  const addSectionTitle = (title: string) => {
    checkPageBreak(25);
    doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.roundedRect(margin - 2, yPos - 5, pageWidth - margin * 2 + 4, 10, 2, 2, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(title, margin + 2, yPos);
    yPos += lineHeight * 2.2;
  };

  const addTextBlock = (label: string, content: string) => {
    if (!content?.trim()) return;
    checkPageBreak(15);

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(label, margin + 2, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const maxWidth = pageWidth - margin * 2 - 8;
    const lines = doc.splitTextToSize(content, maxWidth);

    // Render line by line to handle page breaks for long texts
    lines.forEach((line: string) => {
      checkPageBreak(lineHeight + 2);
      doc.text(line, margin + 4, yPos);
      yPos += lineHeight;
    });
    yPos += 4;
  };

  const addCheckedItems = (
    checkboxes: { id: string; label: string }[],
    checkedState: Record<string, any>
  ) => {
    const checkedItems = checkboxes.filter((cb) => checkedState[cb.id] === true);
    if (checkedItems.length === 0) return;

    checkedItems.forEach((item) => {
      const maxWidth = pageWidth - margin * 2 - 15;
      const lines = doc.splitTextToSize(item.label, maxWidth);
      checkPageBreak(lines.length * lineHeight + 4);

      doc.setFontSize(9);
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text("-", margin + 4, yPos);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(lines, margin + 10, yPos);
      yPos += lines.length * lineHeight + 2;
    });
    yPos += 4;
  };

  // === HEADER ===
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.rect(0, 0, pageWidth, 65, "F");
  doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.rect(0, 65, pageWidth, 3, "F");

  try {
    doc.addImage(logoImage, "PNG", pageWidth / 2 - 18, 6, 36, 36);
  } catch (error) {
    console.log("Logo non charge");
  }

  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(BRAND.name.line1, pageWidth / 2, 48, { align: "center" });
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.name.line2, pageWidth / 2, 55, { align: "center" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(`${BRAND.baseline.parts.text1} & ${BRAND.baseline.parts.text2}`, pageWidth / 2, 62, { align: "center" });

  yPos = 78;

  // === TITLE ===
  doc.setFontSize(16);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  doc.text("PROGRAMME D'HYGIENE VITALE PERSONNALISE", pageWidth / 2, yPos, { align: "center" });
  yPos += 10;

  // === PRACTITIONER INFO ===
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text("Laura ELIE - Naturopathe", pageWidth / 2, yPos, { align: "center" });
  yPos += 5;
  doc.setFontSize(8);
  doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
  doc.text("31 rue de l'ancien petit fosse, 31450 Montesquieu-Lauragais", pageWidth / 2, yPos, { align: "center" });
  yPos += 4;
  doc.text("Tel : 06 07 08 09 10 - Email : contact@regard-de-nature.fr", pageWidth / 2, yPos, { align: "center" });
  yPos += 10;

  // === PATIENT INFO BOX ===
  // Calculate age and format date
  let ageStr = "";
  let dobStr = "";
  if (patientDateOfBirth) {
    const dob = new Date(patientDateOfBirth);
    dobStr = dob.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    ageStr = `${age} ans`;
  }

  const genderLabel = patientGender === "homme" ? "Homme" : patientGender === "femme" ? "Femme" : "";

  const infoLines: string[] = [];
  if (childInfo) {
    infoLines.push(`Enfant : ${childInfo.name}${childInfo.age ? ` (${childInfo.age})` : ""}${childInfo.gender ? ` - ${childInfo.gender}` : ""}`);
    infoLines.push(`Parent/Responsable : ${patientName}`);
    infoLines.push(`Accompagnement : ${accompanimentTitle}`);
  } else {
    infoLines.push(`Consultant : ${patientName}${genderLabel ? ` (${genderLabel})` : ""}${ageStr ? ` - ${ageStr}` : ""}${dobStr ? ` - Né(e) le ${dobStr}` : ""}`);
    infoLines.push(`Accompagnement : ${accompanimentTitle}`);
  }

  const infoBoxHeight = infoLines.length * 8 + 6;
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, infoBoxHeight, 3, 3, "F");
  doc.setFontSize(10);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");

  infoLines.forEach((line, i) => {
    if (i === 0) {
      doc.text(line, margin + 5, yPos + 8);
    } else {
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(line, margin + 5, yPos + 8 + i * 8);
    }
  });

  doc.setFontSize(9);
  doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
  const dateStr = new Date(reportDate).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Date : ${dateStr}`, pageWidth - margin - 5, yPos + 8, { align: "right" });
  yPos += infoBoxHeight + 12;

  // === OBJECTIF ===
  addSectionTitle("OBJECTIF DE L'ACCOMPAGNEMENT");
  addTextBlock("Motif principal de la consultation :", data.motif_principal);
  addTextBlock("Objectif(s) defini(s) avec le consultant :", data.objectifs_definis);

  // === AXE 1 ===
  addSectionTitle("AXE 1 - HYGIENE VITALE");
  addTextBlock("Objectif :", data.axe1_objectif);
  addTextBlock("Conseils :", data.axe1_conseils);
  addCheckedItems(AXE1_CHECKBOXES, data.axe1_checkboxes);

  // === AXE 2 ===
  addSectionTitle("AXE 2 - ALIMENTATION");
  addTextBlock("Objectif :", data.axe2_objectif);
  addTextBlock("Conseils :", data.axe2_conseils);
  addCheckedItems(AXE2_CHECKBOXES, data.axe2_checkboxes);

  // === AXE 3 ===
  addSectionTitle("AXE 3 - ACTIVITE PHYSIQUE");
  addTextBlock("Objectif :", data.axe3_objectif);
  addTextBlock("Recommandations :", data.axe3_recommandations);
  addCheckedItems(AXE3_CHECKBOXES, data.axe3_checkboxes);

  // === COMPLÉMENTS ===
  const hasComplements = data.complements.some(c => c.nom?.trim());
  if (hasComplements) {
    addSectionTitle("COMPLEMENTS / SOUTIENS NATURELS");

    data.complements.forEach((comp, idx) => {
      if (!comp.nom?.trim()) return;

      checkPageBreak(35);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
      doc.text(`Complement ${idx + 1}`, margin + 2, yPos);
      yPos += lineHeight;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);

      const compFields = [
        { label: "Nom", value: comp.nom },
        { label: "Forme", value: comp.forme },
        { label: "Posologie", value: comp.posologie },
        { label: "Moment de prise", value: comp.moment_prise },
        { label: "Duree", value: comp.duree },
      ];

      compFields.forEach(({ label, value }) => {
        if (!value) return;
        const maxWidth = pageWidth - margin * 2 - 10;
        const lines = doc.splitTextToSize(`- ${label} : ${value}`, maxWidth);
        checkPageBreak(lines.length * lineHeight + 2);
        doc.text(lines, margin + 4, yPos);
        yPos += lines.length * lineHeight;
      });
      yPos += 4;
    });
  }

  // === AUTRES ===
  const hasAutres = data.analyses_biologiques?.trim() ||
    data.ressources_conseillees?.trim() ||
    data.orientations_professionnels?.trim() ||
    data.remarques_complementaires?.trim();

  if (hasAutres) {
    addSectionTitle("AUTRES");
    addTextBlock("Analyses biologiques (en accord avec le medecin) :", data.analyses_biologiques);
    addTextBlock("Ressources conseillees :", data.ressources_conseillees);
    addTextBlock("Orientations vers d'autres professionnels :", data.orientations_professionnels);
    addTextBlock("Remarques complementaires :", data.remarques_complementaires);
  }

  // === NOTES ===
  if (notes?.trim()) {
    addSectionTitle("NOTES COMPLEMENTAIRES");
    const maxWidth = pageWidth - margin * 2 - 8;
    const lines = doc.splitTextToSize(notes, maxWidth);
    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    const boxHeight = lines.length * lineHeight + 6;
    checkPageBreak(boxHeight + 10);
    doc.roundedRect(margin + 2, yPos - 3, pageWidth - margin * 2 - 4, boxHeight, 2, 2, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    lines.forEach((line: string) => {
      doc.text(line, margin + 6, yPos + 2);
      yPos += lineHeight;
    });
    yPos += 10;
  }

  // === SIGNATURE ===
  if (signatureDataUrl) {
    checkPageBreak(50);
    yPos += 10;
    doc.setFontSize(10);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.setFont("helvetica", "bold");
    doc.text("Naturopathe", margin, yPos);
    yPos += 8;
    try {
      doc.addImage(signatureDataUrl, "PNG", margin, yPos, 50, 25);
      yPos += 30;
    } catch (error) {
      console.log("Signature non chargee");
    }
  }

  // === MENTIONS LÉGALES ===
  checkPageBreak(60);
  yPos += 10;

  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  const legalBoxY = yPos;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Mentions legales", margin + 4, yPos + 6);
  yPos += 14;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);

  const legalTexts = [
    "Etablissement non medical.",
    "Les conseils proposes dans ce document s'inscrivent dans une demarche d'accompagnement en naturopathie et d'hygiene de vie.",
    "Ils ne se substituent en aucun cas a un suivi medical, a un diagnostic ou a un traitement prescrit par un professionnel de sante.",
    "",
    "Si votre medecin vous a prescrit des medicaments ou des soins, lui seul est habilite a en modifier la posologie ou l'arret.",
    "",
    "Les conseils d'hygiene et de qualite de vie ainsi que les complements alimentaires mentionnes ne pretendent a aucun traitement de maladie et ne donnent pas lieu a un remboursement par la Securite sociale.",
  ];

  const maxWidth = pageWidth - margin * 2 - 8;
  legalTexts.forEach((text) => {
    if (text === "") {
      yPos += 3;
    } else {
      const lines = doc.splitTextToSize(text, maxWidth);
      checkPageBreak(lines.length * 4 + 2);
      doc.text(lines, margin + 4, yPos);
      yPos += lines.length * 4;
    }
  });

  const legalBoxHeight = yPos - legalBoxY + 4;
  doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, legalBoxY - 2, pageWidth - margin * 2, legalBoxHeight, 2, 2, "S");

  // === FOOTER ===
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(colors.accent.r, colors.accent.g, colors.accent.b);
    doc.setLineWidth(1);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setFontSize(8);
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} sur ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(BRAND.legal.confidential, margin, pageHeight - 8);
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(`${BRAND.name.full} - ${BRAND.baseline.full}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  return doc;
};

export const generateVitalityReportPDF = (options: VitalityReportPdfOptions): Uint8Array => {
  const doc = buildVitalityPdf(options);
  return doc.output("arraybuffer") as unknown as Uint8Array;
};

export const downloadVitalityReportPDF = (options: VitalityReportPdfOptions) => {
  const doc = buildVitalityPdf(options);
  const patientName = options.patientName;
  const reportDate = options.reportDate;
  doc.save(`bilan_vitalite_${patientName.replace(/\s+/g, "_")}_${reportDate}.pdf`);
};
